import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import ReactDOM, { type Root } from 'react-dom/client';
import html from 'htm';
import { io, Socket } from 'socket.io-client';
import { Widget as WidgetComponent } from './components/ui/Widget';
import { generateId, sanitizeMessage } from '@peptalk/shared';

import {
  Contact,
  Message,
  WebSocketMessage,
  ApiResponse,
  WidgetActionsObjectSchema,
} from './types';
import { Config } from './types/config';
import {
  initializeTracking,
  getTrackingData,
  clearTrackingData,
  getUserContext,
  mergeConfig,
  generateThemeCSS,
  initializeGTM,
  trackWidgetLoaded,
  trackChatStarted,
  trackLeadGenerated,
  trackFormFillingStarted,
  trackFormSubmitted,
  trackEnquirySubmitted,
  trackWidgetMessageSubmitted,
} from './utils';
import type { GTMCapturedData } from './utils';
import { resolveStringOrPageContextFunction } from './utils/page-context';
import { getCurrencyForCountryCode } from './utils/format';
import { WidgetProvider, WidgetContext } from './hooks/stores/store-context';
import {
  useChatStore,
  useConfigStore,
  useContextStore,
  useInterfaceStore,
  useWidgetStore,
} from './hooks/stores';
import { ContextEngine } from './features/ContextEngine';
import { StoreApi } from 'zustand';
import { WidgetStoreState } from './hooks/stores/use-widget-store';
import { ActionFunctionParams, createWidgetActionHandlers } from './hooks';
import { detectUserLocation } from './utils/location-detection';

// Interface for Socket.IO typing event data
interface TypingData {
  sessionId: string;
  isTyping: boolean;
}

// Extend HTMLElement to include our custom style property
interface HTMLElementWithStyle extends HTMLElement {
  _peptalkStyle?: HTMLStyleElement;
  _peptalkCleanup?: () => void;
}

// React Widget Component (internal)
const ReactWidget: React.FC = () => {
  const config = useConfigStore();
  const {
    sessionId,
    sessionToken,
    setSessionId,
    setSessionToken,
    setConversationId,
    connectionStatus,
    setConnectionStatus,
    setIsTyping,
    addMessages,
    clearMessages,
    filledQuestionnaireData,
    setFilledQuestionnaireData,
    clearSystemMessages,
    setIsFormSubmitting,
    isFormSubmitting,
    lockWelcomeMessageAndContext,
    incrementUnreadCount,
  } = useChatStore();
  const { pageContext, customContext: contextStoreCustomContext } =
    useContextStore();
  const { userCountryCode, setUserCountryCode, setIsDetecting } =
    useWidgetStore(state => state.location);
  const instanceId = useWidgetStore(state => state.instanceId);
  const { widgetIsOpen, isInlineMode } = useInterfaceStore();

  // Read customContext from config (passed during init) or from context store (set via action)
  const customContext = config.customContext || contextStoreCustomContext;

  // State machine: determine if we should skip questionnaire
  // ProposalConversation state (has proposalId) = auto-skip questionnaire
  // NewInquiry state (no proposalId) = show questionnaire (unless config overrides)
  const isProposalConversation = !!customContext?.proposalId;

  // Log widget mode on mount (removed for production)

  // Get store instance for accessing current state in async functions
  const store = useContext(WidgetContext);
  if (!store) throw new Error('Missing WidgetContext.Provider in the tree');

  // Connection
  const socketRef = useRef<Socket | null>(null);
  const [serviceAvailable, setServiceAvailable] = useState(false);

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // GTM form tracking refs
  const formSessionIdRef = useRef<string>(generateId());
  const formFillingTrackedRef = useRef<boolean>(false);
  const lastSessionErrorLoggedRef = useRef<boolean>(false);
  const lastFetchedSessionIdRef = useRef<string | null>(null);
  const recoveryAttemptsRef = useRef<number>(0);

  // Tab away: after this duration hidden, emit user_away so backend routes to both email + widget (timer value is configurable)
  const TAB_AWAY_AFTER_MS = 3 * 60 * 1000;
  const tabLeftAtRef = useRef<number | null>(null);
  const tabAwayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = sessionId;
  const prevWidgetIsOpenRef = useRef<boolean | undefined>(undefined);

  // Emit user_away when widget is closed (panel minimized), widget_opened when opened; same routing as tab away
  useEffect(() => {
    const prev = prevWidgetIsOpenRef.current;
    prevWidgetIsOpenRef.current = widgetIsOpen;
    if (prev === undefined) return; // skip initial mount
    const sid = sessionIdRef.current;
    if (prev === true && widgetIsOpen === false) {
      if (!socketRef.current?.connected || !sid) return;
      socketRef.current.emit('user_away', { sessionId: sid });
    } else if (prev === false && widgetIsOpen === true) {
      trackWidgetLoaded(instanceId, {
        timestamp: Date.now(),
        attribution: getTrackingData(),
      });
      if (!socketRef.current?.connected || !sid) return;
      socketRef.current.emit('widget_opened', { sessionId: sid });
    }
  }, [instanceId, widgetIsOpen]);

  // Detect user location on mount
  useEffect(() => {
    const detectLocation = async () => {
      setIsDetecting(true);
      try {
        const result = await detectUserLocation();
        setUserCountryCode(result.countryCode);
      } catch (error) {
        console.error('[Widget] Failed to detect user location:', error);
      } finally {
        setIsDetecting(false);
      }
    };

    detectLocation();
  }, []); // Run once on mount

  // Check service availability on mount (but don't auto-connect socket)
  useEffect(() => {
    // State is automatically restored by Zustand persist middleware

    // Check service availability immediately
    checkServiceAvailability();

    // Page Visibility API: emit user_away after tab hidden for TAB_AWAY_AFTER_MS; user back = tab_visible clears it
    const handleVisibilityChange = () => {
      const currentSessionId = sessionIdRef.current;
      if (
        !document.hidden &&
        socketRef.current?.connected &&
        currentSessionId
      ) {
        tabLeftAtRef.current = null;
        if (tabAwayTimeoutRef.current) {
          clearTimeout(tabAwayTimeoutRef.current);
          tabAwayTimeoutRef.current = null;
        }
        socketRef.current.emit('tab_visibility', {
          type: 'tab_visible',
          sessionId: currentSessionId,
          timestamp: Date.now(),
        });
      } else if (
        document.hidden &&
        socketRef.current?.connected &&
        currentSessionId
      ) {
        tabLeftAtRef.current = Date.now();
        socketRef.current.emit('tab_visibility', {
          type: 'tab_hidden',
          sessionId: currentSessionId,
          timestamp: tabLeftAtRef.current,
        });
        tabAwayTimeoutRef.current = setTimeout(() => {
          if (socketRef.current?.connected && sessionIdRef.current) {
            socketRef.current.emit('user_away', {
              sessionId: sessionIdRef.current,
            });
          }
          tabAwayTimeoutRef.current = null;
        }, TAB_AWAY_AFTER_MS);
      }
    };

    // Explicitly disconnect socket when tab/window is closing
    const handleBeforeUnload = () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (tabAwayTimeoutRef.current) {
        clearTimeout(tabAwayTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const checkServiceAvailability = async (): Promise<void> => {
    try {
      const url = `${config.apiUrl}/health`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(3000), // 3 second timeout
      });

      if (response.ok) {
        setServiceAvailable(true);
      } else {
        throw new Error(`Service unavailable: ${response.status}`);
      }
    } catch (error) {
      console.error('[Widget] Service health check failed:', error);
      setServiceAvailable(false);
    }
  };

  /**
   * Submit a new inquiry form - creates a new conversation with contact info
   */
  const submitNewInquiryForm = async (
    contactData: Contact
  ): Promise<{
    conversationId: string;
    sessionId: string;
    sessionToken: string;
    trackGtm?: boolean;
    trackLeadGenerated?: boolean;
  }> => {
    return submitToBackend('NewInquiry', `/api/chat/submit-form`, {
      contact: contactData,
    });
  };

  /**
   * Reconnect to an existing conversation by email.
   * Called by the auto-recovery flow when the JWT has expired but form data remains.
   * Returns null if no prior conversation exists (caller should fall back to /submit-form).
   */
  const reconnectExistingSession = async (
    email: string
  ): Promise<{
    conversationId: string;
    sessionId: string;
    sessionToken: string;
  } | null> => {
    try {
      const url = `${config.apiUrl}/api/chat/reconnect`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: AbortSignal.timeout(15000),
      });

      if (response.status === 404) {
        return null; // No prior conversation - caller should create a new one
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Reconnect failed');
      }

      return {
        conversationId: data.data.conversationId,
        sessionId: data.data.session.id,
        sessionToken: data.data.sessionToken,
      };
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('HTTP 404')) {
        return null;
      }
      console.error('[Widget] Reconnect attempt failed:', error);
      return null; // Treat all errors as "no session found" to fall back safely
    }
  };

  /**
   * Continue an existing proposal conversation - no form data needed
   * Backend looks up conversation by proposalId and creates authenticated session
   */
  const continueProposalConversation = async (
    proposalId: string
  ): Promise<{
    conversationId: string;
    sessionId: string;
    sessionToken: string;
  }> => {
    return submitToBackend(
      'ProposalConversation',
      `/api/chat/conversations/${proposalId}/continue`
    );
  };

  /**
   * Generic backend submission for both new inquiries and proposal continuations
   * Consolidates duplicated fetch/validation logic
   */
  const submitToBackend = async (
    flowType: 'NewInquiry' | 'ProposalConversation',
    endpoint: string,
    payload?: { contact: Contact }
  ): Promise<{
    conversationId: string;
    sessionId: string;
    sessionToken: string;
    trackGtm?: boolean;
    trackLeadGenerated?: boolean;
  }> => {
    const flowLabel =
      flowType === 'NewInquiry' ? 'new inquiry' : 'proposal continuation';

    try {
      const url = `${config.apiUrl}${endpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload ? JSON.stringify(payload) : undefined,
        signal: AbortSignal.timeout(30000),
      });

      if (!response.ok) {
        if (response.status === 404 && flowType === 'ProposalConversation') {
          throw new Error(
            'Proposal conversation not found. The conversation may not exist yet.'
          );
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || `Failed to ${flowLabel}`);
      }

      // Validate response has required fields
      if (
        !data.data.session ||
        !data.data.sessionToken ||
        !data.data.conversationId
      ) {
        console.error('⚠️ [Widget] Invalid API response:', {
          hasSession: !!data.data.session,
          hasSessionToken: !!data.data.sessionToken,
          hasConversationId: !!data.data.conversationId,
          responseKeys: Object.keys(data.data),
        });
        throw new Error('Invalid API response: missing required fields');
      }

      return {
        conversationId: data.data.conversationId,
        sessionId: data.data.session.id,
        sessionToken: data.data.sessionToken,
        trackGtm: data.data.trackGtm,
        trackLeadGenerated: data.data.trackLeadGenerated,
      };
    } catch (error) {
      console.error(`[Widget] Failed to submit ${flowLabel}:`, error);
      handleNetworkError(error);
      throw error;
    }
  };

  /**
   * Helper to handle network errors consistently
   */
  const handleNetworkError = (error: unknown) => {
    // Only set error status for actual network failures
    // Don't set for business logic errors (400, 401, 404, 500, etc.)
    if (
      error instanceof TypeError ||
      (error instanceof DOMException && error.name === 'AbortError')
    ) {
      // Timeout error - likely network issue
      setConnectionStatus('error');
    }
    // For HTTP errors (response.ok === false), don't change connection status
    // The service is responding, just with an error
  };

  const connectSocket = (authData?: { sessionId: string; token: string }) => {
    // Use provided values or fall back to state
    const activeSessionId = authData?.sessionId || sessionId;
    const activeToken = authData?.token || sessionToken;

    if (!activeToken || !activeSessionId) {
      console.error('Cannot connect socket: missing token or sessionId');
      return;
    }

    // Don't create a new socket if already connected or mid-handshake
    if (socketRef.current?.connected || socketRef.current?.active) {
      return;
    }

    // Clean up existing socket only if disconnected
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    setConnectionStatus('connecting');
    clearSystemMessages();

    socketRef.current = io(config.apiUrl, {
      auth: { token: activeToken },
      transports: ['polling', 'websocket'], // Start with polling, allow upgrade to WebSocket
      upgrade: true, // Allow upgrade attempt from polling to WebSocket
      rememberUpgrade: false, // Always start with polling on reconnect (don't skip to WebSocket)
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000, // Reduced to 10s since polling connects quickly
    });

    // Monitor transport upgrades (polling → WebSocket)
    socketRef.current.io.engine.on('upgrade', () => {
      // Successfully upgraded to WebSocket
    });

    // If WebSocket upgrade fails, stick with polling
    socketRef.current.io.engine.on('upgradeError', error => {
      console.warn(
        '[Widget] ⚠️ WebSocket upgrade failed, staying on polling:',
        error.message
      );
      // Prevent future upgrade attempts - stick with polling
      if (socketRef.current) {
        socketRef.current.io.opts.transports = ['polling'];
      }
    });

    // Helper function to start error timeout after connection failures
    const startErrorTimeout = () => {
      if (!errorTimeoutRef.current) {
        errorTimeoutRef.current = setTimeout(() => {
          console.warn(
            '[Widget] Connection timeout - setting disconnected status'
          );
          // Don't clear session on timeout - let Socket.IO keep retrying
          // Only clear session on explicit auth failure (handled in connect_error)
          setConnectionStatus('disconnected');
          errorTimeoutRef.current = null;
        }, 30000); // Increased to 30s to allow more time for retries
      }
    };

    socketRef.current?.on('connect', () => {
      setConnectionStatus('connected');

      // Clear error timeout since connection succeeded
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }

      // Reset recovery attempts on successful connection
      recoveryAttemptsRef.current = 0;
    });

    socketRef.current?.on('disconnect', reason => {
      // Don't change status for transport close (normal during upgrade to websocket)
      if (reason !== 'transport close') {
        setConnectionStatus('disconnected');
      }

      if (reason === 'io server disconnect') {
        setTimeout(() => {
          if (socketRef.current && !socketRef.current.connected) {
            socketRef.current.connect();
          }
        }, 1000);
      }
    });

    socketRef.current?.on('connect_error', error => {
      console.error('[Widget] Socket connection error:', error.message);

      // Check if server explicitly denied connection (won't auto-reconnect)
      if (socketRef.current && !socketRef.current.active) {
        console.error(
          '[Widget] Connection denied by server - clearing invalid session'
        );

        // Clear invalid session from localStorage to force creating a new one
        setSessionId(null);
        setSessionToken(null);
        lastFetchedSessionIdRef.current = null;

        // Disconnect the socket to stop retry attempts
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }

        // Set disconnected status (not error) - service is available, just session was invalid
        setConnectionStatus('disconnected');
        return;
      }

      // Temporary failure - Socket.IO will try transports (polling → websocket) and retry
      startErrorTimeout();
    });

    socketRef.current?.on('message', (data: WebSocketMessage) => {
      handleSocketMessage(data);
    });

    socketRef.current?.on('typing', (data: TypingData) => {
      if (data.sessionId === activeSessionId) {
        setIsTyping(data.isTyping);
      }
    });

    socketRef.current?.on('reconnect', _attemptNumber => {
      setConnectionStatus('connected');

      // Clear error timeout since reconnection succeeded
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    });

    socketRef.current?.on('reconnect_error', error => {
      console.error('[Widget] Reconnection attempt failed:', error.message);
      // Don't set error immediately - Socket.IO is retrying with all transports
      startErrorTimeout();
    });

    socketRef.current?.on('pong', () => {
      // Heartbeat response received
    });
  };

  const handleSocketMessage = (data: WebSocketMessage) => {
    switch (data.type) {
      case 'message':
        addMessages([data.data]);
        if (
          data.data.sender === 'agent' &&
          !store.getState().interface.widgetIsOpen
        ) {
          incrementUnreadCount();
        }
        break;

      case 'session_update':
        if (sessionId && data.sessionId === sessionId) {
          if (data.data.status === 'closed') {
            addSystemMessage('Chat session has been closed');
          } else if (data.data.status === 'conversation_linked') {
            // Front conversation ID is now stored in Supabase and looked up by session
            // No need to update local session state
          }
        }
        break;

      case 'error':
        addErrorMessage(data.data.error || 'An error occurred');
        break;

      default:
        console.warn('[Widget] Unknown socket message type:', data.type);
    }
  };

  const addSystemMessage = (content: string) => {
    const message: Message = {
      id: generateId(),
      content,
      timestamp: Date.now(),
      sender: 'system',
      sessionId: sessionId || 'system',
      groupId: socketRef.current?.id,
    };

    addMessages([message]);
  };

  const addErrorMessage = (content: string) => {
    addSystemMessage(`Error: ${content}`);
  };

  // Create contact data with standard fields + all form data generically
  // Note: Hidden field functions are resolved in Questionnaire component before submission
  // This function ensures all hidden fields are included in formData, even if empty (function failed)
  const createValidatedContactData = (
    formData: Record<string, string>,
    isAnonymous: boolean = false,
    widgetConfig: Config.Widget = config
  ): Contact => {
    const trackingData = getTrackingData();
    const userContext = getUserContext();

    // Standard contact fields
    const validatedContact: Contact = {
      isAnonymous,
    };

    // Extract only core contact fields to top level
    if (formData.name) validatedContact.name = formData.name;
    if (formData.email) validatedContact.email = formData.email;
    if (formData.phone) validatedContact.phone = formData.phone;

    // Tracking parameters
    if (trackingData.impactCode)
      validatedContact.impactCode = trackingData.impactCode;
    if (trackingData.gclid) validatedContact.gclid = trackingData.gclid;
    if (trackingData.msclkid) validatedContact.msclkid = trackingData.msclkid;
    if (trackingData.gbraid) validatedContact.gbraid = trackingData.gbraid;
    if (trackingData.wbraid) validatedContact.wbraid = trackingData.wbraid;
    if (trackingData.fbclid) validatedContact.fbclid = trackingData.fbclid;
    if (trackingData.liFatId) validatedContact.liFatId = trackingData.liFatId;
    if (trackingData.rdtCid) validatedContact.rdtCid = trackingData.rdtCid;
    if (trackingData.dclid) validatedContact.dclid = trackingData.dclid;
    if (trackingData.utmSource)
      validatedContact.utmSource = trackingData.utmSource;
    if (trackingData.utmMedium)
      validatedContact.utmMedium = trackingData.utmMedium;
    if (trackingData.utmCampaign)
      validatedContact.utmCampaign = trackingData.utmCampaign;
    if (trackingData.utmTerm) validatedContact.utmTerm = trackingData.utmTerm;
    if (trackingData.utmContent)
      validatedContact.utmContent = trackingData.utmContent;

    // User context information
    if (userContext.timezone) validatedContact.timezone = userContext.timezone;
    if (userContext.timezoneOffset !== undefined)
      validatedContact.timezoneOffset = userContext.timezoneOffset;
    if (userContext.language) validatedContact.language = userContext.language;
    if (userContext.userAgent)
      validatedContact.userAgent = userContext.userAgent;
    if (userContext.referrer) validatedContact.referrer = userContext.referrer;
    if (userContext.pageUrl) validatedContact.pageUrl = userContext.pageUrl;

    // Widget mode: track whether user used inline form or floating widget
    validatedContact.widgetMode = isInlineMode ? 'inline-form' : 'widget';

    // Currency detected from user's country code (ISO 4217)
    const currencySymbol = getCurrencyForCountryCode(userCountryCode);
    const symbolToIso: Record<string, string> = {
      '€': 'EUR',
      '£': 'GBP',
      'C$': 'CAD',
      $: 'USD',
    };
    validatedContact.detectedCurrency = symbolToIso[currencySymbol] || 'USD';

    // Generic form data - include ALL form fields (including dynamic ones like 'details', 'date', etc.)
    // This ensures any configurable form fields are captured and sent to Front.com
    // Hidden fields should always be included, even if empty (for tracking/debugging)

    // Identify hidden and function fields from config
    const hiddenAndFunctionFieldNames = new Set(
      widgetConfig.features.chat.fields
        .filter(f => f.type === 'hidden' || f.type === 'function')
        .map(f => f.name)
    );

    const cleanFormData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(formData)) {
      const isHiddenOrFunctionField = hiddenAndFunctionFieldNames.has(key);
      const isEmpty = value === undefined || value === null || value === '';

      // Always include hidden/function fields, even if empty (function may have failed)
      // Skip empty regular fields
      if (!isEmpty || isHiddenOrFunctionField) {
        cleanFormData[key] = value || ''; // Ensure hidden/function fields are at least empty string
      }
    }

    if (Object.keys(cleanFormData).length > 0) {
      validatedContact.formData = cleanFormData;
    }

    return validatedContact;
  };

  /**
   * Initialize conversation - routes to appropriate flow based on conversation type
   *
   * Two distinct flows:
   * 1. New Inquiry: User fills questionnaire → creates new conversation
   * 2. Proposal Continuation: No questionnaire → continues existing conversation within the scope of a proposal client facing page
   */
  const initializeConversation = async (formData: Record<string, string>) => {
    // Track form submission GTM event (fires regardless of lead qualification)
    const gtmSubmitData: GTMCapturedData = {
      timestamp: Date.now(),
      attribution: getTrackingData(),
    };
    if (isInlineMode) {
      trackFormSubmitted(
        instanceId,
        { name: formData.name, email: formData.email },
        gtmSubmitData,
        formSessionIdRef.current
      );
    } else {
      trackEnquirySubmitted(
        instanceId,
        { name: formData.name, email: formData.email },
        gtmSubmitData,
        formSessionIdRef.current
      );
    }

    // Set loading state
    setIsFormSubmitting(true);
    setIsTyping(true); // Show typing indicator

    try {
      // Route to appropriate flow based on conversation type
      if (isProposalConversation) {
        await handleProposalContinuation();
      } else {
        await handleNewInquirySubmission(formData);
      }

      // Hide typing indicator - qualification message will arrive via socket
      setIsTyping(false);

      // Auto-continue to chat after brief delay
      setTimeout(() => {
        handleContinueToChat();
      }, 1500);
    } catch (error) {
      console.error('[Widget] Form submission failed:', error);

      // Hide typing indicator before showing error
      setIsTyping(false);

      // Show user-friendly error message
      showErrorMessage();
      setConnectionStatus('error');

      // Only clear form data on specific fatal errors where recovery is impossible
      // e.g. 400 Bad Request (invalid data), 401/403 Auth failures (session invalid)
      // Do NOT clear on 500s or network errors - let user retry
      const isFatalError =
        error instanceof Error &&
        (error.message.includes('400') ||
          error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('validation failed'));

      if (isFatalError) {
        console.warn(
          '[Widget] Fatal error - clearing form data for fresh start',
          error
        );
        setFilledQuestionnaireData({});
      } else {
        console.warn(
          '[Widget] Recoverable error - preserving form data for retry',
          error
        );
      }
    } finally {
      setIsFormSubmitting(false);
    }
  };

  // Auto-trigger proposal continuation when proposalId is present
  // This connects the socket immediately for returning users
  const hasTriggeredProposalContinuationRef = useRef(false);
  const hasTriggeredRecoveryRef = useRef(false);
  const proposalId = customContext.proposalId;

  // Auto-connect for Proposal Conversations
  useEffect(() => {
    const shouldAutoConnect =
      isProposalConversation &&
      !sessionId &&
      !hasTriggeredProposalContinuationRef.current &&
      proposalId;

    if (shouldAutoConnect) {
      hasTriggeredProposalContinuationRef.current = true;
      // Use the same flow as form submission
      initializeConversation({});
    }
  }, [isProposalConversation, sessionId, proposalId, initializeConversation]);

  // Auto-recover for New Inquiries (Regular Chat)
  // If session is lost (e.g. expired JWT, backend restart) but form data remains,
  // try to reconnect to the existing Front.com conversation before creating a new one.
  // This prevents duplicate conversations from being created for returning users.
  useEffect(() => {
    const hasFormData = Object.keys(filledQuestionnaireData).length > 0;
    const shouldRecover =
      !isProposalConversation &&
      !sessionId &&
      hasFormData &&
      !isFormSubmitting && // Prevent race condition during active submission
      !hasTriggeredRecoveryRef.current;

    if (shouldRecover) {
      // Check if we've tried to recover too many times (loop detection)
      if (recoveryAttemptsRef.current >= 3) {
        console.error(
          '[Widget] Auto-recovery loop detected (3+ failed attempts). Forcing fresh start.'
        );
        hasTriggeredRecoveryRef.current = true;
        recoveryAttemptsRef.current = 0;
        setFilledQuestionnaireData({});
        setSessionId(null);
        setSessionToken(null);
        setConnectionStatus('disconnected');
        return;
      }

      console.log(
        `[Widget] Session lost but form data exists - attempting recovery (attempt ${
          recoveryAttemptsRef.current + 1
        })`
      );
      recoveryAttemptsRef.current += 1;
      hasTriggeredRecoveryRef.current = true;

      const email = filledQuestionnaireData.email;

      if (email) {
        // Try to reconnect to the existing conversation first, so we never create
        // a duplicate Front.com conversation for a returning user whose JWT expired.
        reconnectExistingSession(email)
          .then(reconnected => {
            if (reconnected) {
              console.log('[Widget] Reconnected to existing conversation');
              setSessionId(reconnected.sessionId);
              setSessionToken(reconnected.sessionToken);
              setConversationId(reconnected.conversationId);
              lastFetchedSessionIdRef.current = null; // Allow history fetch on next continue
              connectSocket({
                sessionId: reconnected.sessionId,
                token: reconnected.sessionToken,
              });
            } else {
              // No prior conversation found — genuinely a new user
              initializeConversation(filledQuestionnaireData);
            }
          })
          .catch(() => {
            // Reconnect endpoint unreachable — fall back to creating new conversation
            initializeConversation(filledQuestionnaireData);
          });
      } else {
        // No email in form data — can only create a new conversation
        initializeConversation(filledQuestionnaireData);
      }
    }
  }, [
    isProposalConversation,
    sessionId,
    filledQuestionnaireData,
    initializeConversation,
    isFormSubmitting,
  ]);

  /**
   * Flow 1: Handle new inquiry submission
   * User filled out the questionnaire, creating a brand new conversation
   */
  const handleNewInquirySubmission = async (
    formData: Record<string, string>
  ) => {
    // Capture GTM data BEFORE clearing (preserves original timestamp and attribution)
    const gtmCapturedData: GTMCapturedData = {
      timestamp: Date.now(),
      attribution: getTrackingData(),
    };

    // Save form data to state (for UI reference)
    setFilledQuestionnaireData(formData);

    // Lock welcome message and context at form submission time
    // This ensures changes to welcome message/context don't affect already-submitted forms
    const welcomeMessage = config.features?.chat?.welcomeMessage;
    const resolvedWelcomeMessage = resolveStringOrPageContextFunction({
      value: welcomeMessage,
      pageContext,
      customContext,
    });

    // Lock the resolved welcome message text and current custom context
    if (resolvedWelcomeMessage) {
      lockWelcomeMessageAndContext(resolvedWelcomeMessage, customContext);
    }

    // Create contact data with tracking info
    const contactData = createValidatedContactData(formData, false);

    // Clear tracking data after capturing it
    clearTrackingData();

    // Submit to backend - creates new conversation
    const {
      conversationId,
      sessionId: newSessionId,
      sessionToken: newToken,
      trackGtm,
      trackLeadGenerated: shouldTrackLeadGenerated,
    } = await submitNewInquiryForm(contactData);

    // Track GTM events — chat_started fires for all qualified submissions (widget only),
    // lead_generated only fires when backend confirms lead was created
    if (trackGtm && !isInlineMode) {
      trackChatStarted(instanceId, gtmCapturedData);
    }
    if (shouldTrackLeadGenerated) {
      trackLeadGenerated(
        instanceId,
        { name: formData.name, email: formData.email },
        gtmCapturedData
      );
    }

    // Update session credentials
    setSessionId(newSessionId);
    setSessionToken(newToken);
    setConversationId(conversationId);

    // Connect socket immediately to receive qualification message
    // Backend sends qualification message after 2.5s, so we need socket ready ASAP
    connectSocket({
      sessionId: newSessionId,
      token: newToken,
    });

    // Mark this session as initialized - no history to fetch (brand new conversation)
    lastFetchedSessionIdRef.current = newSessionId;

    // New conversations start empty - no history to fetch
    // First message (qualification message) will arrive via socket after ~2.5s
  };

  /**
   * Flow 2: Handle proposal conversation continuation
   * User is continuing an existing conversation - NO form submission needed
   * Backend already has all contact/proposal data from original conversation
   */
  const handleProposalContinuation = async () => {
    const proposalId = customContext.proposalId as string;

    // Backend looks up existing conversation by proposalId
    // No contact data needed - it's already in the system
    const {
      conversationId,
      sessionId: newSessionId,
      sessionToken: newToken,
    } = await continueProposalConversation(proposalId);

    // Update session credentials
    setSessionId(newSessionId);
    setSessionToken(newToken);
    setConversationId(conversationId);

    // Connect socket immediately for proposal continuation
    connectSocket({
      sessionId: newSessionId,
      token: newToken,
    });

    // Fetch conversation history to load existing messages
    const totalMessageCount = await fetchConversationHistory(conversationId);

    // Add contextual welcome message acknowledging they're viewing the proposal
    // Place AFTER history so it appears at the bottom (most recent)
    const proposalTitle = customContext.proposalTitle || 'this proposal';
    const historyNote =
      totalMessageCount && totalMessageCount > 3
        ? ` (Showing 3 most recent messages from your conversation history.)`
        : '';

    const welcomeMessage: Message = {
      id: generateId(),
      content: `Hi! I can see you're viewing ${proposalTitle}. How can I help you with the speakers or your event?${historyNote}`,
      timestamp: Date.now(),
      sender: 'agent',
      sessionId: 'welcome-back',
    };
    addMessages([welcomeMessage]);

    // Mark this session as initialized - history already fetched in this function
    lastFetchedSessionIdRef.current = newSessionId;
  };

  /**
   * Show user-friendly error message in chat
   */
  const showErrorMessage = () => {
    const errorMessageText = resolveStringOrPageContextFunction({
      value: config.features.chat.errorMessage,
      pageContext,
      customContext,
    });

    const finalErrorMessage =
      errorMessageText?.replace('{{contactEmail}}', config.contactEmail) ||
      `Oops! We're having trouble processing your request right now. Please try again in a moment, or if the problem persists, you can email us directly at ${config.contactEmail} and we'll get back to you as soon as possible.`;

    addMessages([
      {
        id: generateId(),
        content: finalErrorMessage,
        timestamp: Date.now(),
        sender: 'agent',
        sessionId: sessionId || 'system',
        groupId: socketRef.current?.id,
      },
    ]);
  };
  const fetchConversationHistory = async (
    conversationId?: string
  ): Promise<number> => {
    try {
      const currentSessionToken = store.getState().chat.sessionToken;
      const currentSessionId = store.getState().chat.sessionId;

      if (!currentSessionToken) {
        console.error('[Widget] No session token available for history fetch');
        return 0;
      }

      // If conversationId is a pending ID (cnv_pending_*), use session endpoint instead
      // The session endpoint will look up the real conversation ID from the database
      // (the pending ID gets resolved when Front.com webhooks update the correlation)
      const isPendingConversation =
        conversationId && conversationId.startsWith('cnv_pending_');
      const useSessionEndpoint =
        (!conversationId || isPendingConversation) && currentSessionId;

      if (isPendingConversation) {
        console.log(
          '[Widget] Pending conversation ID detected - using session endpoint to get real ID'
        );
      }

      const endpoint = useSessionEndpoint
        ? `${config.apiUrl}/api/chat/history/session/${currentSessionId}`
        : `${config.apiUrl}/api/chat/history/${conversationId}`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSessionToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch history: ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      // Get current messages to preserve welcome/system messages
      const currentMessages = store.getState().chat.messages;

      // Preserve welcome and system messages (they have specific sessionIds)
      const localMessages = currentMessages.filter(
        msg =>
          msg.sessionId === 'welcome' ||
          msg.sessionId === 'welcome-back' ||
          msg.sender === 'system'
      );

      // Clear all messages first
      clearMessages();

      // Re-add local messages (welcome, system) first
      if (localMessages.length > 0) {
        addMessages(localMessages);
      }

      // Then add conversation history from Front.com
      // Only show 3 most recent messages to keep chat clean
      if (data.success && data.data.messages && data.data.messages.length > 0) {
        const allMessages = data.data.messages;
        const recentMessages = allMessages.slice(-3); // Get last 3 messages

        addMessages(recentMessages);

        return allMessages.length; // Return total count for welcome message
      } else {
        return 0;
      }
    } catch (error) {
      console.error('[Widget] Failed to fetch conversation history:', error);
      // Don't throw - failing to load history shouldn't break the chat
      return 0;
    }
  };

  const handleContinueToChat = async () => {
    // Get current values from store to avoid stale closure
    const currentSessionId = store.getState().chat.sessionId;
    const currentSessionToken = store.getState().chat.sessionToken;
    const currentConversationId = store.getState().chat.conversationId;

    try {
      // Session must exist from form submission - no recreation allowed per TDD
      if (!currentSessionId || !currentSessionToken) {
        // Only log error once to avoid spam on repeated clicks
        if (!lastSessionErrorLoggedRef.current) {
          console.error(
            '[Widget] No session available for chat continuation - user must submit form first'
          );
          lastSessionErrorLoggedRef.current = true;
        }
        setConnectionStatus('error');
        return;
      }

      // Reset error flag when session is available
      lastSessionErrorLoggedRef.current = false;

      // Fetch conversation history for returning users only
      // Skip if:
      // - Proposal mode (history already fetched in handleProposalContinuation)
      // - Session just initialized (marked by lastFetchedSessionIdRef)
      // Use sessionId endpoint if conversationId is not available
      const isReturningUser =
        currentSessionId &&
        lastFetchedSessionIdRef.current !== currentSessionId;
      const needsHistoryFetch =
        isReturningUser &&
        !isProposalConversation &&
        (currentConversationId || currentSessionId);

      if (needsHistoryFetch) {
        await fetchConversationHistory(currentConversationId || undefined);
        lastFetchedSessionIdRef.current = currentSessionId;
      }

      // Connect socket with existing session (if not already connected)
      if (!socketRef.current || !socketRef.current.connected) {
        connectSocket({
          sessionId: currentSessionId,
          token: currentSessionToken,
        });
      }
    } catch (error) {
      console.error('Failed to continue to chat:', error);
      setConnectionStatus('error');
    }
  };

  const sendMessageToServer = async (content: string) => {
    // Get current values from store to avoid stale closure
    const currentSessionId = store.getState().chat.sessionId;
    const currentSessionToken = store.getState().chat.sessionToken;

    // Session must exist from form submission - no anonymous messaging per TDD
    if (!currentSessionId || !currentSessionToken) {
      console.error(
        '[Widget] No session available for messaging - user must submit form first'
      );
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSessionToken}`,
        },
        body: JSON.stringify({
          content: content,
          sessionId: currentSessionId,
        }),
      });

      const data: ApiResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('[Widget] Failed to send message:', error);
      addErrorMessage('Failed to send message. Please try again.');
    }
  };

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!sessionId || connectionStatus !== 'connected') {
        return;
      }

      const message: Message = {
        id: generateId(),
        content: sanitizeMessage(content),
        timestamp: Date.now(),
        sender: 'user',
        sessionId: sessionId,
      };

      addMessages([message]);
      trackWidgetMessageSubmitted(instanceId);
      await sendMessageToServer(content);
    },
    [sessionId, connectionStatus, addMessages]
  );

  const handleTyping = (typing: boolean) => {
    if (!socketRef.current || !sessionId) return;

    socketRef.current.emit('message', {
      type: 'typing',
      isTyping: typing,
      sessionId: sessionId,
    });
  };

  const handleMessageSeen = (frontMessageId: string) => {
    if (!socketRef.current || !sessionId) return;
    socketRef.current.emit('message', {
      type: 'message_seen',
      messageId: frontMessageId,
    });
  };

  // Track first form field interaction for GTM
  const handleFormInteraction = useCallback(() => {
    if (formFillingTrackedRef.current) return;
    formFillingTrackedRef.current = true;
    if (isInlineMode) {
      trackFormFillingStarted(instanceId, formSessionIdRef.current);
    }
  }, [instanceId]);

  return (
    <WidgetComponent
      onSendMessage={handleSendMessage}
      onTyping={handleTyping}
      onFormSubmit={initializeConversation}
      onFormInteraction={handleFormInteraction}
      onContinueToChat={handleContinueToChat}
      onMessageSeen={handleMessageSeen}
      serviceAvailable={serviceAvailable}
    />
  );
};

// Widget class that maintains the same external API but uses React internally
export class Widget {
  private readonly config: Config.Widget;
  private readonly container: HTMLElementWithStyle;
  private store: StoreApi<WidgetStoreState> | null;
  private root!: Root;
  public readonly instanceId: string;

  // Multi-instance support
  private static instanceCounter: number = 0;
  private static instances: Map<string, Widget> = new Map();

  public static readonly html = html.bind(React.createElement);

  private constructor(config: Config.UserWidget, instanceId: string) {
    this.instanceId = instanceId;
    this.config = mergeConfig(config);
    this.container = this.createContainer();
    this.store = null;
    this.render();
  }

  /**
   * Initialize a new widget instance.
   *
   * @param config - Widget configuration
   * @param options - Optional configuration for multi-instance support
   * @returns Widget instance
   *
   * @example
   * // Single widget (auto-generates instance ID)
   * Peptalk.init({ ui: { position: 'bottom-right' } });
   *
   * @example
   * // Multi-instance with explicit IDs
   * Peptalk.init({ ui: { target: '#form' } }, { instanceId: 'inline-form' });
   * Peptalk.init({ ui: { position: 'bottom-right' } }, { instanceId: 'floating' });
   */
  public static init(
    config: Config.UserWidget,
    options?: { instanceId?: string }
  ): Widget {
    const instanceId =
      options?.instanceId || `widget-${++Widget.instanceCounter}`;

    // If instance with this ID exists, destroy it first
    if (Widget.instances.has(instanceId)) {
      console.warn(
        `Widget "${instanceId}" already exists. Destroying previous instance.`
      );
      Widget.instances.get(instanceId)!.destroy();
    }

    // Initialize tracking only once globally (first instance)
    if (Widget.instances.size === 0) {
      initializeTracking();

      // Initialize GTM with config from the first widget
      const mergedConfig = mergeConfig(config);
      const isDevEnv = [
        'localhost',
        '127.0.0.1',
        'assets-dev.peptalk.cc',
        'peptalk-new.webflow.io',
      ].includes(window.location.hostname);

      initializeGTM({
        enabled: isDevEnv ? false : (mergedConfig.gtm?.enabled ?? false),
        containerId: mergedConfig.gtm?.containerId,
        loadIfMissing: mergedConfig.gtm?.loadIfMissing ?? false,
      });
    }

    const widget = new Widget(config, instanceId);
    Widget.instances.set(instanceId, widget);

    return widget;
  }

  /**
   * Get a specific widget instance by ID.
   * If no ID provided, returns the first instance (backwards compatibility).
   */
  public static getInstance(instanceId?: string): Widget | null {
    if (instanceId) {
      return Widget.instances.get(instanceId) || null;
    }
    // Return first instance for backwards compatibility
    const firstInstance = Widget.instances.values().next().value;
    return firstInstance || null;
  }

  /**
   * Get all active widget instances.
   */
  public static getInstances(): Map<string, Widget> {
    return new Map(Widget.instances);
  }

  /**
   * Destroy widget instance(s).
   * If instanceId provided, destroys that specific instance.
   * If no instanceId, destroys all instances.
   */
  public static destroy(instanceId?: string): void {
    if (instanceId) {
      const instance = Widget.instances.get(instanceId);
      if (instance) {
        instance.destroy();
      }
    } else {
      // Destroy all instances
      Widget.instances.forEach(instance => instance.destroy());
    }
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    // Use instance-specific class name to allow different themes per instance
    const containerClass = `peptalk-widget-container peptalk-widget-${this.instanceId}`;
    container.className = containerClass;

    // Apply position attribute from config
    const position = this.config.ui?.position || 'bottom-right';
    container.setAttribute('data-position', position);
    container.setAttribute('data-instance-id', this.instanceId);

    // Apply theme CSS variables to this specific instance's container
    const themeCSS = generateThemeCSS(this.config.theme);
    const style = document.createElement('style');
    const styleId = `peptalk-theme-styles-${this.instanceId}`;
    style.setAttribute('id', styleId);
    // Target only this instance's container using instance-specific class
    style.textContent = `.peptalk-widget-${this.instanceId} { ${themeCSS} }`;

    // Always add instance-specific styles (each instance has its own)
    document.head.appendChild(style);

    // Store style reference for cleanup
    (container as HTMLElementWithStyle)._peptalkStyle = style;

    // Add scroll event prevention to the container
    const preventScrollPropagation = (e: Event) => {
      e.stopPropagation();
    };

    container.addEventListener('scroll', preventScrollPropagation, {
      passive: false,
    });
    container.addEventListener('wheel', preventScrollPropagation, {
      passive: false,
    });
    container.addEventListener('touchmove', preventScrollPropagation, {
      passive: false,
    });

    let targetElement: HTMLElement | null = document.body;

    const hasTarget = !!this.config.ui?.target;
    let foundTarget = false;

    if (hasTarget) {
      if (typeof this.config.ui.target === 'string') {
        // Try to find the target element
        const foundElement = document.querySelector(
          this.config.ui.target
        ) as HTMLElement;

        if (foundElement) {
          // Verify it's actually an HTMLElement
          if (foundElement instanceof HTMLElement) {
            targetElement = foundElement;
            foundTarget = true;
          } else {
            console.error(
              `[Peptalk Widget] Target selector "${this.config.ui.target}" found a non-HTML element. Falling back to document.body.`
            );
          }
        } else {
          console.warn(
            `[Peptalk Widget] Target element "${this.config.ui.target}" not found in DOM. Falling back to document.body.`
          );
        }
      } else if (this.config.ui.target instanceof HTMLElement) {
        targetElement = this.config.ui.target;
        foundTarget = true;
      } else {
        console.error(
          `[Peptalk Widget] Invalid target type. Expected string or HTMLElement, got:`,
          typeof this.config.ui.target
        );
      }
    }

    // Set target mode attribute and inline styles before placing container
    if (hasTarget && foundTarget) {
      container.setAttribute('data-target-mode', 'true');
      // Apply inline styles directly using setProperty with 'important' flag
      // This ensures inline rendering even if CSS hasn't loaded yet
      container.style.setProperty('position', 'static', 'important');
      container.style.setProperty('bottom', 'auto', 'important');
      container.style.setProperty('right', 'auto', 'important');
      container.style.setProperty('top', 'auto', 'important');
      container.style.setProperty('left', 'auto', 'important');
      // Fill width, allow height to grow with content
      container.style.setProperty('min-height', '100%', 'important');
      container.style.setProperty('height', 'auto', 'important');
      container.style.setProperty('width', '100%', 'important');
      container.style.setProperty('max-width', 'none', 'important');
      container.style.setProperty('max-height', 'none', 'important');
      container.style.setProperty('margin', '0', 'important');
      container.style.setProperty('padding', '0', 'important');
      container.style.setProperty('z-index', 'auto', 'important');
      container.style.setProperty('display', 'flex', 'important');
      container.style.setProperty('flex-direction', 'column', 'important');
    }

    // Place container in target or fallback to body
    if (hasTarget && foundTarget) {
      try {
        // Clear any existing content and add container
        targetElement.replaceChildren(container);
        // Preserve box-sizing for proper layout
        const existingStyle = targetElement.getAttribute('style') || '';
        const boxSizingStyle = 'box-sizing: content-box !important;';
        if (!existingStyle.includes('box-sizing')) {
          targetElement.setAttribute(
            'style',
            `${existingStyle} ${boxSizingStyle}`.trim()
          );
        }
      } catch (error) {
        console.error(
          `[Peptalk Widget] Failed to place container in target element:`,
          error
        );
        // Fallback to body
        if (document.body) {
          document.body.appendChild(container);
        } else {
          document.addEventListener('DOMContentLoaded', () => document.body.appendChild(container), { once: true });
        }
        console.warn(
          `[Peptalk Widget] Container placed in document.body as fallback`
        );
      }
    } else if (targetElement) {
      targetElement.appendChild(container);
    } else {
      // DOM not ready (script loaded in <head>) — defer until body exists
      document.addEventListener('DOMContentLoaded', () => document.body.appendChild(container), { once: true });
    }

    return container;
  }

  private render() {
    this.root = ReactDOM.createRoot(this.container);
    this.root.render(
      <WidgetProvider
        config={this.config}
        instanceId={this.instanceId}
        onRender={store => {
          this.store = store;

          // Set up monitoring to ensure container stays in target div
          if (
            this.config.ui?.target &&
            this.container.hasAttribute('data-target-mode')
          ) {
            const targetSelector =
              typeof this.config.ui.target === 'string'
                ? this.config.ui.target
                : null;

            if (targetSelector) {
              const ensureContainerInTarget = () => {
                const targetElement = document.querySelector(
                  targetSelector
                ) as HTMLElement;
                const containerParent = this.container.parentElement;

                if (targetElement && containerParent !== targetElement) {
                  console.warn(
                    `[Peptalk Widget] Container moved out of target! Current parent:`,
                    containerParent,
                    `Moving back to:`,
                    targetSelector
                  );
                  targetElement.replaceChildren(this.container);

                  // Re-apply inline styles to container
                  this.container.style.setProperty(
                    'position',
                    'static',
                    'important'
                  );
                  this.container.style.setProperty(
                    'bottom',
                    'auto',
                    'important'
                  );
                  this.container.style.setProperty(
                    'right',
                    'auto',
                    'important'
                  );
                  this.container.style.setProperty('top', 'auto', 'important');
                  this.container.style.setProperty('left', 'auto', 'important');
                  this.container.style.setProperty(
                    'min-height',
                    '100%',
                    'important'
                  );
                  this.container.style.setProperty(
                    'height',
                    'auto',
                    'important'
                  );
                  this.container.style.setProperty(
                    'width',
                    '100%',
                    'important'
                  );
                }

                // Also ensure chat window or inline form wrapper has correct styles
                const widgetWrapper = this.container.querySelector(
                  '.peptalk-chat-window, .peptalk-inline-form-wrapper'
                ) as HTMLElement;
                if (widgetWrapper) {
                  widgetWrapper.style.setProperty(
                    'position',
                    'static',
                    'important'
                  );
                  widgetWrapper.style.setProperty(
                    'bottom',
                    'auto',
                    'important'
                  );
                  widgetWrapper.style.setProperty('right', 'auto', 'important');
                  widgetWrapper.style.setProperty('top', 'auto', 'important');
                  widgetWrapper.style.setProperty('left', 'auto', 'important');
                  widgetWrapper.style.setProperty('width', '100%', 'important');
                  widgetWrapper.style.setProperty(
                    'min-height',
                    '100%',
                    'important'
                  );
                  widgetWrapper.style.setProperty(
                    'height',
                    'auto',
                    'important'
                  );
                  widgetWrapper.style.setProperty(
                    'max-width',
                    '100%',
                    'important'
                  );
                  widgetWrapper.style.setProperty(
                    'max-height',
                    'none',
                    'important'
                  );
                  widgetWrapper.style.setProperty(
                    'transform',
                    'none',
                    'important'
                  );
                  widgetWrapper.style.setProperty('margin', '0', 'important');
                }
              };

              // Check immediately
              ensureContainerInTarget();

              // Set up MutationObserver to watch for container being moved
              const observer = new MutationObserver(() => {
                ensureContainerInTarget();
              });

              // Watch the target element and document body for changes
              const targetElement = document.querySelector(targetSelector);
              if (targetElement) {
                observer.observe(targetElement, {
                  childList: true,
                  subtree: true,
                });
              }
              observer.observe(document.body, {
                childList: true,
                subtree: true,
              });

              // Also check periodically as a fallback
              const checkInterval = setInterval(() => {
                ensureContainerInTarget();
              }, 1000);

              // Store cleanup function
              this.container._peptalkCleanup = () => {
                observer.disconnect();
                clearInterval(checkInterval);
              };
            }
          }
        }}
      >
        <ContextEngine instanceId={this.instanceId} />
        <ReactWidget />
      </WidgetProvider>
    );
  }

  /**
   * Send a message through the widget
   * Used by external Widget.action({ action: 'send-message' }) calls
   */
  public async sendMessage(content: string): Promise<void> {
    if (!this.store) {
      console.error('[Widget] Cannot send message - store not initialized');
      return;
    }

    const state = this.store.getState();
    const { sessionId, connectionStatus } = state.chat;

    if (!sessionId || connectionStatus !== 'connected') {
      console.warn(
        '[Widget] Cannot send message - missing session or not connected:',
        {
          sessionId: sessionId ? 'present' : 'missing',
          connectionStatus,
        }
      );
      return;
    }

    // Add message to local state
    const message: Message = {
      id: generateId(),
      content: sanitizeMessage(content),
      timestamp: Date.now(),
      sender: 'user',
      sessionId: sessionId,
    };

    state.chat.addMessages([message]);

    // Send to server via REST API
    const currentSessionToken = state.chat.sessionToken;
    if (!currentSessionToken) {
      console.error('[Widget] No session token available');
      return;
    }

    try {
      const response = await fetch(`${this.config.apiUrl}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSessionToken}`,
        },
        body: JSON.stringify({
          content,
          sessionId,
        }),
      });

      const data: ApiResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('[Widget] Failed to send message:', error);
      // Add error message to chat
      const errorMessage: Message = {
        id: generateId(),
        content: 'Error: Failed to send message. Please try again.',
        timestamp: Date.now(),
        sender: 'system',
        sessionId: sessionId || 'system',
      };
      state.chat.addMessages([errorMessage]);
    }
  }

  /**
   * Update the welcome message for this widget instance.
   *
   * @param welcomeMessage - The welcome message (string or function)
   *
   * @example
   * const widget = Peptalk.init({ /* config *\/ });
   * widget.setWelcomeMessage('New welcome message!');
   *
   * @example
   * // With dynamic function
   * widget.setWelcomeMessage((pageContext, customContext) => {
   *   const expertName = customContext?.expertName || 'this speaker';
   *   return `Hi! Interested in booking ${expertName}?`;
   * });
   */
  public setWelcomeMessage(
    welcomeMessage:
      | string
      | ((pageContext: unknown, customContext: unknown) => string)
  ): void {
    if (!this.store) {
      console.error(
        '[Widget] Cannot set welcome message - store not initialized'
      );
      return;
    }

    const state = this.store.getState();
    const { filledQuestionnaireData, sessionId } = state.chat;

    // Check if form has been submitted (has form data or session)
    const hasFormSubmitted =
      Object.keys(filledQuestionnaireData).length > 0 || !!sessionId;

    if (hasFormSubmitted) {
      console.warn(
        '[Widget] Cannot update welcome message after form submission. The welcome message is locked for this session. Clear the session to start a new one.'
      );
      return;
    }

    state.setWelcomeMessage(welcomeMessage);
  }

  /**
   * Update the custom context for this widget instance.
   *
   * @param customContext - The custom context object
   *
   * @example
   * const widget = Peptalk.init({ /* config *\/ });
   * widget.setContext({
   *   expertName: 'Tim Ferriss',
   *   proposalId: '123'
   * });
   */
  public setContext(customContext: Record<string, unknown>): void {
    if (!this.store) {
      console.error('[Widget] Cannot set context - store not initialized');
      return;
    }

    const state = this.store.getState();
    const { filledQuestionnaireData, sessionId } = state.chat;

    // Check if form has been submitted (has form data or session)
    const hasFormSubmitted =
      Object.keys(filledQuestionnaireData).length > 0 || !!sessionId;

    if (hasFormSubmitted) {
      console.warn(
        '[Widget] Cannot update context after form submission. The context is locked for this session. Clear the session to start a new one.'
      );
      return;
    }

    state.context.setCustomContext(customContext);
  }

  /**
   * Execute an action on widget instance(s).
   *
   * @param params - Action parameters
   * @param options - Optional instance targeting
   *
   * @example
   * // Target first instance (backwards compatible)
   * Peptalk.action({ action: 'open-widget' });
   *
   * @example
   * // Target specific instance
   * Peptalk.action({ action: 'open-widget' }, { instanceId: 'floating' });
   *
   * @example
   * // Broadcast to all instances
   * Peptalk.action({ action: 'clear-session' }, { broadcast: true });
   */
  public static action(
    params: ActionFunctionParams,
    options?: { instanceId?: string; broadcast?: boolean }
  ): void {
    const result = WidgetActionsObjectSchema.safeParse(params);
    if (!result.success) {
      console.error('Invalid Peptalk.action() parameters:', result.error);
      return;
    }

    // Broadcast to all instances
    if (options?.broadcast) {
      Widget.instances.forEach(instance => {
        if (instance.store) {
          const actionHandlers = createWidgetActionHandlers({
            store: instance.store,
            sendMessageCallback: (message: string) =>
              instance.sendMessage(message),
          });
          actionHandlers.executeAction(params);
        }
      });
      return;
    }

    // Target specific instance or first instance
    const instance = options?.instanceId
      ? Widget.getInstance(options.instanceId)
      : Widget.getInstance();

    if (!instance) {
      const target = options?.instanceId
        ? `"${options.instanceId}"`
        : 'any instance';
      console.warn(
        `Peptalk ${target} not initialized. Call Peptalk.init() first.`
      );
      return;
    }

    if (!instance.store) {
      console.warn(
        `Peptalk "${instance.instanceId}" store not initialized. Call Peptalk.init() first.`
      );
      return;
    }

    const actionHandlers = createWidgetActionHandlers({
      store: instance.store,
      sendMessageCallback: (message: string) => instance.sendMessage(message),
    });

    actionHandlers.executeAction(params);
  }

  public destroy() {
    if (this.root) {
      this.root.unmount();
    }

    // Clean up theme styles
    if (this.container?._peptalkStyle) {
      const style = this.container._peptalkStyle;
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }

    // Clean up MutationObserver and interval if they exist
    if (this.container._peptalkCleanup) {
      this.container._peptalkCleanup();
    }

    this.container?.parentNode?.removeChild(this.container);

    // Remove from instances registry
    Widget.instances.delete(this.instanceId);
  }
}
